import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import { bool, node } from 'prop-types';
import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { compose } from 'redux';

import {
  FieldSelect,
  FieldTextInput,
  FieldRadioButton,
  Form,
  PrimaryButton,
  StripePaymentAddress,
} from '../../../components';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { useConfiguration } from '../../../context/configurationContext';

import ReCAPTCHA from "react-google-recaptcha";
import { occupationOptions } from '../../../config/configUser';
import css from './SignupForm.module.css';

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        form,
        termsAndConditions,
        values,
      } = fieldRenderProps;

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );
      // occupation
      const occupationRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.occupationRequired',
        })
      );

      // customDisplayName
      const customDisplayNameRequired = validators.required(
        intl.formatMessage({
          id: `SignupForm.customDisplayNameRequired.${values.accountType}`,
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      const accountTypeSelected = !!values.accountType;
      const isCompany = values.accountType == 'company';

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress || !accountTypeSelected;
      const config = useConfiguration();

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={intl.formatMessage({
                id: 'SignupForm.emailLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.emailPlaceholder',
              })}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            <div className={css.name}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.fname` : 'fname'}
                name="fname"
                autoComplete="given-name"
                label={intl.formatMessage({
                  id: 'SignupForm.firstNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.firstNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.firstNameRequired',
                  })
                )}
              />
              <FieldTextInput
                className={css.lastNameRoot}
                type="text"
                id={formId ? `${formId}.lname` : 'lname'}
                name="lname"
                autoComplete="family-name"
                label={intl.formatMessage({
                  id: 'SignupForm.lastNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.lastNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.lastNameRequired',
                  })
                )}
              />
            </div>
            <FieldSelect
              id={`${formId}.occupation`}
              className={css.inputFields}
              name="occupation"
              label={intl.formatMessage({ id: 'SignupForm.occupationLabel' })}
              validate={occupationRequired}
            >
              <option value="">{intl.formatMessage({
                id: 'OccupationInput.placeholder'
              })}</option>
              {occupationOptions.map((occupationOption, index) => <option value={occupationOption} key={index}>{intl.formatMessage({
                id: `OccupationInput.option.${occupationOption}`
              })}</option>)}
            </FieldSelect>

            <div className={css.radioButtons}>
              <label>
                <FormattedMessage id="SignupForm.accountType.label" />
              </label>
              <FieldRadioButton
                id={`${formId}.accountType.company`}
                name="accountType"
                label={intl.formatMessage({ id: 'SignupForm.accountType.company' })}
                value="company"
                showAsRequired={true}
                className={css.signupRadio}
              />
              <FieldRadioButton
                id={`${formId}.accountType.individual`}
                name="accountType"
                label={intl.formatMessage({ id: 'SignupForm.accountType.individual' })}
                value="individual"
                showAsRequired={true}
                className={css.signupRadio}
              />
            </div>

            {accountTypeSelected ? <>
              <FieldTextInput
                type="text"
                className={css.inputFields}
                id={formId ? `${formId}.customDisplayName` : 'customDisplayName'}
                name="customDisplayName"
                label={intl.formatMessage({
                  id: `SignupForm.customDisplayNameLabel.${values.accountType}`,
                })}
                placeholder={intl.formatMessage({
                  id: `SignupForm.customDisplayNamePlaceholder.${values.accountType}`,
                })}
                subDescription={isCompany ? null : intl.formatMessage({
                  id: `SignupForm.customDisplayNameSubDescription.${values.accountType}`,
                })}
                validate={customDisplayNameRequired}
              />

              {isCompany ? <FieldTextInput
                type="text"
                className={css.inputFields}
                id={formId ? `${formId}.businessId` : 'businessId'}
                name="businessId"
                label={intl.formatMessage({
                  id: 'SignupForm.businessIdLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.businessIdPlaceholder',
                })}
                validate={validators.validBusinessId(
                  intl.formatMessage({
                    id: 'SignupForm.businessIdValidation'
                  })
                )}
              /> : null}

              <div className={css.addressWrapper}>
                <StripePaymentAddress
                  intl={intl}
                  form={form}
                  fieldId={formId}
                  card={{ update: () => { } }}
                  locale={config.localization.locale}
                />
              </div>

              <FieldTextInput
                className={css.inputFields}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />
              {/* <div className={css.recaptchaStyle}>
                <Field name="recaptcha" validate={validators.required(intl.formatMessage({
                  id: 'RecaptCha',
                }))}>
                  {(props) =>
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                      onChange={props.input.onChange} />
                  }
                </Field>
              </div> */}
            </> : null}
          </div>

          <div
            className={css.bottomWrapper}
            style={accountTypeSelected ? {} : {marginTop: '40px'}}
          >
            {accountTypeSelected ? <>
              {termsAndConditions}
            </> : null}

            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form >
      );
    }}
  />
);

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
